import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider } from './context/ThemeContext';
import { theme } from './theme';
import { useCore } from './hooks/useCore';
import { useData } from './hooks/useData';
import Layout from './components/Layout/Layout';
import LayoutAuth from './components/Layout/LayoutAuth';
import AgreementLayout from './components/Layout/AgreementLayout';
import FileUploadStatus from './components/File/FileUploadStatus';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Contract from './pages/Contract';
import DataFeed from './pages/DataFeed';
import Lookup from './pages/Lookup';
import Reprice from './pages/Reprice';
import Audit from './pages/Audit';
import Team from './pages/Team';
import Organizations from './pages/Organizations';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import FullModal from './components/Layout/FullModal';
import { PrivateRoute } from './components/PrivateRoute';
import { Toaster, toast } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { CoreProvider } from './hooks/useCore';

import { Box, CircularProgress } from '@mui/material';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import FileUploadFullScreen from './components/File/FileUploadFullScreen';
import { TermsOfService, PrivacyPolicy } from './utils/agreements';

const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'var(--bg-main)'
    }}
  >
    <CircularProgress />
  </Box>
);

const AppRoutes = () => {
  const { loading, isAuthenticated, user } = useAuth();
  const isContractUser = user?.roles?.includes('contract');
  const location = useLocation();
  const { handleFilesUpload } = useData();
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Auth Routes */}
      {!isAuthenticated ? (
        <Route element={<LayoutAuth />}>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="*" element={
            location.pathname === "/login"
              ? null
              : <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />
          } />
        </Route>
      ) : (
        /* Protected Routes */
        <Route element={<Layout />}>
          <Route path="/contract" element={<Contract />} />
          {!isContractUser && (
            <>
              <Route path="/" element={<Home />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/file/*" element={<Dashboard />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/data-feed" element={<DataFeed />} />
              <Route path="/lookup" element={<Lookup />} />
              <Route path="/reprice" element={<Reprice />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/team/*" element={<Team />} />
              {user?.roles?.includes('superadmin') && (
                <Route path="/organizations/*" element={<Organizations />} />
              )}
            </>
          )}
          <Route path="*" element={<Navigate to={isContractUser ? "/contract" : "/"} replace />} />
        </Route>
      )}
      <Route element={<AgreementLayout />}>
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Route>
    </Routes>
  );
};

const AuthenticatedApp = () => {
  const { isAuthenticated } = useAuth();
  const { handleFilesUpload } = useData();

  const handleFilesDrop = (files) => {
        
    // Show success toast
    // toast.success(`${files.length} file(s) dropped! Redirecting to upload page...`, {
    //   duration: 3000,
    // });

    handleFilesUpload(files);
    
    // Store files in localStorage temporarily (you might want to use a more robust state management solution)
    // localStorage.setItem('droppedFiles', JSON.stringify(files.map(file => ({
    //   name: file.name,
    //   size: file.size,
    //   type: file.type,
    //   lastModified: file.lastModified
    // }))));
    
    // Redirect to Reprice page after a short delay
    // setTimeout(() => {
    //   window.location.href = '/reprice';
    // }, 1000);
  };

  return (
    <>
      <Suspense fallback={<LoadingScreen />}>
        <AppRoutes />
      </Suspense>
      <FileUploadStatus/>
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--bg-panel)',
            color: 'var(--text-primary)',
            borderRadius: '8px',
            padding: '16px',
            fontSize: '14px',
            fontFamily: '"Space Grotesk", sans-serif',
            border: '1px solid var(--border-color)',
          },
          success: {
            style: {
              background: 'var(--color-green)',
              color: '#fff',
            },
          },
          error: {
            style: {
              background: 'var(--color-red)',
              color: '#fff',
            },
          },
        }}
      />
      <Tooltip
        id="navbar-tooltip"
        style={{
          backgroundColor: '#87cefa',
          color: '#000',
          fontSize: '0.75rem',
          fontWeight: 600,
          padding: '4px 8px',
          borderRadius: '4px',
          zIndex: 9999
        }}
      />
      {isAuthenticated && <FileUploadFullScreen onFilesDrop={handleFilesDrop} />}
    </>
  );
};

function App() {
  // Global 401 error handler
  useEffect(() => {
    const handleUnauthorized = (event) => {
            
      // Generic 401 handling - you can customize this
      localStorage.removeItem("token");
      localStorage.removeItem("org");
      
      // Clear any auth headers
      if (window.client && window.client.api) {
        window.client.api.setHeaders({ Authorization: null });
      }
      
      // You could also show a toast notification here
      // toast('Session expired. Please log in again.', { duration: 4000 });
      
      // Force redirect to login
      window.location.href = '/login';
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  return (
    <Router>
      <MuiThemeProvider theme={theme}>
        <ThemeProvider>
          <AuthenticatedApp />
        </ThemeProvider>
      </MuiThemeProvider>
    </Router>
  );
}

export default App;
